import { Col, Row, Typography, Card, Spin, Empty, Tag, Button, Modal, Form, Input, Select, Switch } from 'antd';
import { CopyOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import { formatDate } from '../../helpers/date';
import OnlineStatusIndicator from '../Items/Status/OnlineStatusIndicator';
import useMultiNotification from '../../hooks/useNotification';
import axios from 'axios';
import { baseURL } from '../../hooks/useAxiosPost';
import { getUserAuthToken } from '../../helpers/userDetails';
import '../Items/Cards/site/singleSiteCard.css';

interface RouterSingleRowWithCopyProps extends SingleRowRouterDetailsProp {
  copyValue?: string;
  onCopy?: (value: string, label: string) => void;
}

const RouterSingleRow: React.FC<RouterSingleRowWithCopyProps> = ({
  title,
  value,
  valueSpecialClass,
  copyValue,
  onCopy,
}) => {
  const handleCopy = () => {
    if (copyValue && onCopy) {
      onCopy(copyValue, title);
    }
  };

  return (
    <Row gutter={[6, 16]}>
      <Col span={6}>
        <div className="router-details-label-container">
          <Typography className="router-details-label">{title}</Typography>
          <Typography className="router-details-label">:</Typography>
        </div>
      </Col>
      <Col span={18}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          <Typography className={`router-details-value ${valueSpecialClass || ''}`} style={{ flex: 1 }}>
            {value || '-'}
          </Typography>
          {copyValue && (
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={handleCopy}
              style={{
                color: '#3A77BC',
                padding: '4px 8px',
                minWidth: 'auto',
              }}
              title={`Copy ${title}`}
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

const MasterStaffRouterDetailsComponent = () => {
  const { intPkSiteCoreRouterID } = useParams();
  const { openNotification, contextHolder } = useMultiNotification();
  const [form] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const postApiDataRef = useRef<any>(null);
  const hasProcessedUpdateSuccess = useRef(false);
  const lastProcessedUpdateResponse = useRef<any>(null);
  const lastProcessedErrorCount = useRef({ router: 0, update: 0 });

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('companies/router-details');

  const [
    updateRouterData,
    updateRouterResponse,
    updateRouterError,
    updateRouterLoading,
    updateRouterMessage,
    updateRouterErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/update-router');

  const [
    postToggleStatusData,
    toggleStatusResponse,
    toggleStatusError,
    toggleStatusLoading,
    toggleStatusMessage,
    toggleStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/toggle-router-status');

  const [fetchVendorsData, vendorsResponse, vendorsError, vendorsLoading, vendorsMessage, vendorsErrorCount] =
    useAxiosPost<ResponseProp>('companies/vendors');

  const [fetchBotsData, botsResponse, botsError, botsLoading, botsMessage, botsErrorCount] =
    useAxiosPost<ResponseProp>('radbot-nodes/all');

  const [attachBotData, attachBotResponse, attachBotError, attachBotLoading, attachBotMessage, attachBotErrorCount] =
    useAxiosPost<ResponseProp>('companies/attach-bot-to-router');

  const [isAttachBotModalOpen, setIsAttachBotModalOpen] = useState(false);
  const [attachBotForm] = Form.useForm();
  const [allBots, setAllBots] = useState<any[]>([]);
  const hasProcessedAttachBotSuccess = useRef(false);
  const lastProcessedAttachBotResponse = useRef<any>(null);
  const lastProcessedAttachBotErrorCount = useRef(0);

  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [pendingEnableValue, setPendingEnableValue] = useState<number | null>(null);
  const hasProcessedToggleSuccess = useRef(false);
  const lastProcessedToggleResponse = useRef<any>(null);
  const lastProcessedToggleErrorCount = useRef(0);

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      openNotification('success', 'Copied!', `${label} copied to clipboard`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        openNotification('success', 'Copied!', `${label} copied to clipboard`);
      } catch (error) {
        openNotification('error', 'Failed to Copy', 'Could not copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadLoginPages = async () => {
    if (!intPkSiteCoreRouterID) {
      openNotification('error', 'Error', 'Router ID not found');
      return;
    }

    // Get router name from routerDetails if available
    const routerName = routerDetails?.routerName;
    let defaultFilename = `router-${intPkSiteCoreRouterID}-login-pages.zip`;
    if (routerName && routerName.trim()) {
      // Sanitize router name for filename (same logic as backend)
      const sanitizedRouterName = routerName
        .trim()
        .replace(/[^a-z0-9]+/gi, '-') // Replace any sequence of non-alphanumeric chars with single hyphen
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
        .toLowerCase();
      if (sanitizedRouterName && sanitizedRouterName.length > 0) {
        defaultFilename = `${sanitizedRouterName}-login-pages.zip`;
      }
    }

    setIsDownloading(true);
    try {
      const authTokenString = getUserAuthToken();
      const response = await axios.post(
        `${baseURL}/companies/download-router-login-pages`,
        {
          intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
          authTokenString: authTokenString,
        },
        {
          headers: {
            Authorization: `Bearer ${authTokenString}`,
          },
          responseType: 'blob',
        },
      );

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header, fallback to router name based filename
      let filename = defaultFilename;

      const contentDisposition =
        response.headers?.['content-disposition'] ||
        response.headers?.['Content-Disposition'] ||
        (response.headers as any)?.common?.['content-disposition'] ||
        (response.headers as any)?.common?.['Content-Disposition'];

      if (contentDisposition) {
        // Try to extract filename from Content-Disposition header
        // Handles both: filename="name.zip" and filename=name.zip
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          // Remove quotes if present and decode if URL-encoded
          filename = filenameMatch[1].replace(/['"]/g, '').trim();
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as-is
          }
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      openNotification('success', 'Download Started', 'Login pages ZIP file download started');
    } catch (error: any) {
      console.error('Download error:', error);
      openNotification('error', 'Download Failed', error.response?.data?.message || 'Failed to download login pages');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    if (intPkSiteCoreRouterID) {
      postApiData({
        intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
      } as any);
      fetchVendorsData({} as any);
      fetchBotsData({} as any);
    }
  }, [intPkSiteCoreRouterID]);

  const [routerDetails, setRouterDetails] = useState<any>(null);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      setRouterDetails(responseData);
    }
  }, [apiResponse]);

  useEffect(() => {
    if (vendorsResponse !== null) {
      const responseData: any = vendorsResponse.responseData;
      if (Array.isArray(responseData)) {
        setVendors(responseData);
      }
    }
  }, [vendorsResponse]);

  useEffect(() => {
    if (botsResponse !== null) {
      const responseData: any = botsResponse.responseData;
      if (Array.isArray(responseData)) {
        setAllBots(responseData);
      }
    }
  }, [botsResponse]);

  // Handle toggle status success
  useEffect(() => {
    if (toggleStatusResponse !== null && toggleStatusResponse !== lastProcessedToggleResponse.current) {
      lastProcessedToggleResponse.current = toggleStatusResponse;
      hasProcessedToggleSuccess.current = true;
      lastProcessedToggleErrorCount.current = toggleStatusErrorCount;

      const backendMessage =
        (toggleStatusResponse as any).message ||
        `Router ${
          (toggleStatusResponse as any)?.responseData?.enable === 1 || pendingEnableValue === 1
            ? 'enabled'
            : 'disabled'
        } successfully`;

      openNotification('success', 'Router Status Updated', backendMessage);
      setIsToggleModalOpen(false);
      setPendingEnableValue(null);

      // Refresh router details
      setTimeout(() => {
        if (intPkSiteCoreRouterID && postApiDataRef.current) {
          postApiDataRef.current({
            intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
          } as any);
        }
      }, 100);
    }
  }, [toggleStatusResponse, intPkSiteCoreRouterID, toggleStatusErrorCount, pendingEnableValue, openNotification]);

  // Reset the flag when toggleStatusResponse becomes null
  useEffect(() => {
    if (toggleStatusResponse === null) {
      hasProcessedToggleSuccess.current = false;
      lastProcessedToggleResponse.current = null;
    }
  }, [toggleStatusResponse]);

  // Handle toggle status error
  useEffect(() => {
    if (
      toggleStatusError !== null &&
      toggleStatusErrorCount !== 0 &&
      lastProcessedToggleErrorCount.current !== toggleStatusErrorCount &&
      !hasProcessedToggleSuccess.current
    ) {
      lastProcessedToggleErrorCount.current = toggleStatusErrorCount;
      openNotification('error', 'Failed to Update Status', toggleStatusError);
      setIsToggleModalOpen(false);
      setPendingEnableValue(null);
    }
  }, [toggleStatusError, toggleStatusErrorCount, openNotification]);

  // Handle attach bot success
  useEffect(() => {
    if (attachBotResponse !== null && attachBotResponse !== lastProcessedAttachBotResponse.current) {
      lastProcessedAttachBotResponse.current = attachBotResponse;
      hasProcessedAttachBotSuccess.current = true;
      lastProcessedAttachBotErrorCount.current = attachBotErrorCount;
      openNotification('success', 'Bot Attached', 'Bot attached to router successfully');
      setIsAttachBotModalOpen(false);
      attachBotForm.resetFields();
      // Refresh router details
      setTimeout(() => {
        if (intPkSiteCoreRouterID && postApiDataRef.current) {
          postApiDataRef.current({
            intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
          } as any);
        }
      }, 100);
    }
  }, [attachBotResponse, intPkSiteCoreRouterID, attachBotErrorCount, openNotification]);

  // Reset the flag when modal opens or attachBotResponse becomes null
  useEffect(() => {
    if (attachBotResponse === null) {
      hasProcessedAttachBotSuccess.current = false;
      lastProcessedAttachBotResponse.current = null;
    }
  }, [attachBotResponse]);

  // Handle attach bot error
  useEffect(() => {
    if (
      attachBotError !== null &&
      attachBotErrorCount !== 0 &&
      lastProcessedAttachBotErrorCount.current !== attachBotErrorCount &&
      !hasProcessedAttachBotSuccess.current
    ) {
      lastProcessedAttachBotErrorCount.current = attachBotErrorCount;
      openNotification('error', 'Failed to Attach Bot', attachBotError);
    }
  }, [attachBotError, attachBotErrorCount, openNotification]);

  // Handle router details error
  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.router !== apiErrorCount) {
      lastProcessedErrorCount.current.router = apiErrorCount;
      openNotification('error', 'Failed to Load Router Details', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Set form initial values when modal opens
  useEffect(() => {
    if (isEditModalOpen && routerDetails) {
      form.setFieldsValue({
        routerName: routerDetails.routerName,
        eUserLocalNetworkLinkLogin: routerDetails.eUserLocalNetworkLinkLogin,
        coreRouterIp: routerDetails.coreRouterIp,
        password: routerDetails.password,
        username: routerDetails.username,
        intPkCoreRouterVendorsID: routerDetails.intPkCoreRouterVendorsID,
        voucherAuthBotSecret: routerDetails.voucherAuthBotSecret,
        eUserInitProxyURL: routerDetails.eUserInitProxyURL,
        eUserVoucherCheckProxyURL: routerDetails.eUserVoucherCheckProxyURL,
        eUserOpenPageAfterSuccessLogin: routerDetails.eUserOpenPageAfterSuccessLogin || '',
        routerCryptoKey: routerDetails.routerCryptoKey,
      });
    }
  }, [isEditModalOpen, routerDetails, form]);

  // Handle update success
  useEffect(() => {
    if (updateRouterResponse !== null && updateRouterResponse !== lastProcessedUpdateResponse.current) {
      lastProcessedUpdateResponse.current = updateRouterResponse;
      hasProcessedUpdateSuccess.current = true;
      lastProcessedErrorCount.current.update = updateRouterErrorCount;
      openNotification('success', 'Router Updated', 'Router details updated successfully');
      setIsEditModalOpen(false);
      form.resetFields();
      // Refresh router details
      setTimeout(() => {
        if (intPkSiteCoreRouterID && postApiDataRef.current) {
          postApiDataRef.current({
            intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
          } as any);
        }
      }, 100);
    }
  }, [updateRouterResponse, intPkSiteCoreRouterID]);

  // Reset the flag when modal opens or updateRouterResponse becomes null
  useEffect(() => {
    if (updateRouterResponse === null) {
      hasProcessedUpdateSuccess.current = false;
      lastProcessedUpdateResponse.current = null;
    }
  }, [updateRouterResponse]);

  // Handle update error
  useEffect(() => {
    if (
      updateRouterError !== null &&
      updateRouterErrorCount !== 0 &&
      lastProcessedErrorCount.current.update !== updateRouterErrorCount &&
      !hasProcessedUpdateSuccess.current
    ) {
      lastProcessedErrorCount.current.update = updateRouterErrorCount;
      openNotification('error', 'Failed to Update Router', updateRouterError);
    }
  }, [updateRouterError, updateRouterErrorCount, openNotification]);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCancel = () => {
    if (!updateRouterLoading) {
      setIsEditModalOpen(false);
      form.resetFields();
    }
  };

  const handleUpdate = (values: any) => {
    const routerData = {
      intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
      routerName: values.routerName.trim(),
      eUserLocalNetworkLinkLogin: values.eUserLocalNetworkLinkLogin.trim(),
      coreRouterIp: values.coreRouterIp.trim(),
      password: values.password.trim(),
      username: values.username.trim(),
      intPkCoreRouterVendorsID: Number(values.intPkCoreRouterVendorsID),
      voucherAuthBotSecret: values.voucherAuthBotSecret.trim(),
      eUserInitProxyURL: values.eUserInitProxyURL.trim(),
      eUserVoucherCheckProxyURL: values.eUserVoucherCheckProxyURL.trim(),
      eUserOpenPageAfterSuccessLogin: values.eUserOpenPageAfterSuccessLogin?.trim() || null,
      routerCryptoKey: values.routerCryptoKey.trim(),
    };
    updateRouterData(routerData as any);
  };

  const handleAttachBotClick = () => {
    setIsAttachBotModalOpen(true);
    // Set current bot if one is attached
    if (routerDetails?.radbotnodes_m2m_site_core_routers?.[0]?.radbot_nodes) {
      attachBotForm.setFieldsValue({
        intPkRadBotNodeID: routerDetails.radbotnodes_m2m_site_core_routers[0].radbot_nodes.intPkRadBotNodeID,
      });
    }
  };

  const handleAttachBotCancel = () => {
    if (!attachBotLoading) {
      setIsAttachBotModalOpen(false);
      attachBotForm.resetFields();
    }
  };

  const handleAttachBot = (values: any) => {
    const botData = {
      intPkSiteCoreRouterID: Number(intPkSiteCoreRouterID),
      intPkRadBotNodeID: Number(values.intPkRadBotNodeID),
    };
    attachBotData(botData as any);
  };

  const handleStatusToggle = (checked: boolean) => {
    const newEnableValue = checked ? 1 : 0;
    setPendingEnableValue(newEnableValue);
    setIsToggleModalOpen(true);
  };

  const handleConfirmToggle = () => {
    if (pendingEnableValue !== null && intPkSiteCoreRouterID) {
      postToggleStatusData({
        id: Number(intPkSiteCoreRouterID),
        enable: pendingEnableValue,
      } as any);
    }
  };

  const handleCancelToggle = () => {
    setIsToggleModalOpen(false);
    setPendingEnableValue(null);
  };

  if (apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Typography style={{ marginTop: '16px', display: 'block' }}>Loading router details...</Typography>
      </div>
    );
  }

  if (apiError && !apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty
          description={<Typography.Text type="danger">{apiError || 'Failed to load router details'}</Typography.Text>}
        />
      </div>
    );
  }

  if (!routerDetails) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty description="Router details not found" />
      </div>
    );
  }

  const {
    routerName,
    coreRouterIp,
    username,
    password,
    routerToken,
    radiusSecret,
    eUserLocalNetworkLinkLogin,
    eUserInitProxyURL,
    eUserVoucherCheckProxyURL,
    eUserOpenPageAfterSuccessLogin,
    voucherAuthBotSecret,
    routerCryptoKey,
    enable,
    dateCreated,
    core_router_vendor,
    company_site,
  } = routerDetails;

  const vendorName = core_router_vendor?.coreRouterVendorName || '-';
  const siteName = company_site?.siteName || '-';
  const companyName = company_site?.company?.companyName || '-';
  const attachedBot = routerDetails?.radbotnodes_m2m_site_core_routers?.[0]?.radbot_nodes || null;

  return (
    <>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* Header Card */}
        <Col span={24}>
          <Card className="border-card">
            <div className="site-card-top-items">
              <div className="site-details-box">
                <div className="site-title-box">
                  <Typography className="site-header-title">{routerName || 'Router Details'}</Typography>
                  <EditOutlined
                    className="cursor-pointer edit-site-icon"
                    onClick={handleEditClick}
                    style={{ fontSize: '18px', color: '#3A77BC' }}
                  />
                </div>
                <div>
                  <Typography className="site-header-address">
                    {siteName} • {companyName}
                  </Typography>
                </div>
              </div>
              <div className="site-status-indicator-box">
                <OnlineStatusIndicator isOnline={Boolean(enable)} />
                <span style={{ marginRight: '12px' }}>{enable ? 'ENABLED' : 'DISABLED'}</span>
                <Tag color={enable ? 'success' : 'default'}>{enable ? 'Active' : 'Disabled'}</Tag>
              </div>
            </div>
          </Card>
        </Col>

        {/* Basic Information Card */}
        <Col span={24}>
          <Card className="border-card" title="Basic Information">
            <RouterSingleRow title="Router Name" value={routerName} copyValue={routerName} onCopy={handleCopy} />
            <RouterSingleRow title="Core Router IP" value={coreRouterIp} copyValue={coreRouterIp} onCopy={handleCopy} />
            <RouterSingleRow title="Vendor" value={vendorName} />
            <RouterSingleRow title="Site Name" value={siteName} />
            <RouterSingleRow title="Company Name" value={companyName} />
            <RouterSingleRow title="Date Created" value={dateCreated ? formatDate(dateCreated) : '-'} />
            <Row gutter={[6, 16]}>
              <Col span={6}>
                <div className="router-details-label-container">
                  <Typography className="router-details-label">Status</Typography>
                  <Typography className="router-details-label">:</Typography>
                </div>
              </Col>
              <Col span={18}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Switch
                    checked={Boolean(enable)}
                    onChange={handleStatusToggle}
                    disabled={toggleStatusLoading}
                    checkedChildren="Enabled"
                    unCheckedChildren="Disabled"
                  />
                  <Tag color={enable ? 'success' : 'default'}>{enable ? 'Active' : 'Disabled'}</Tag>
                </div>
              </Col>
            </Row>
            <Row gutter={[6, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadLoginPages}
                  loading={isDownloading}
                  block
                >
                  Download Login Pages
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Authentication & Credentials Card */}
        <Col span={24}>
          <Card className="border-card" title="Authentication & Credentials">
            <RouterSingleRow title="Username" value={username} copyValue={username} onCopy={handleCopy} />
            <RouterSingleRow
              title="Password"
              value={password ? '••••••••' : '-'}
              copyValue={password}
              onCopy={handleCopy}
            />
            <RouterSingleRow
              title="Router Token"
              value={routerToken || '-'}
              copyValue={routerToken}
              onCopy={handleCopy}
            />
            <RouterSingleRow
              title="Radius Secret"
              value={radiusSecret ? '••••••••' : '-'}
              copyValue={radiusSecret}
              onCopy={handleCopy}
            />
            <RouterSingleRow
              title="Router Crypto Key"
              value={routerCryptoKey ? '••••••••' : '-'}
              copyValue={routerCryptoKey}
              onCopy={handleCopy}
            />
            <RouterSingleRow
              title="Voucher Auth Bot Secret"
              value={voucherAuthBotSecret ? '••••••••' : '-'}
              copyValue={voucherAuthBotSecret}
              onCopy={handleCopy}
            />
          </Card>
        </Col>

        {/* Network & Proxy URLs Card */}
        <Col span={24}>
          <Card className="border-card" title="Network & Proxy URLs">
            <RouterSingleRow
              title="Local Network Link Login"
              value={eUserLocalNetworkLinkLogin}
              valueSpecialClass={eUserLocalNetworkLinkLogin ? 'link-class' : undefined}
              copyValue={eUserLocalNetworkLinkLogin}
              onCopy={handleCopy}
            />
            {eUserLocalNetworkLinkLogin && (
              <Row gutter={[6, 16]} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                <Col span={6}></Col>
                <Col span={18}>
                  <Typography.Link
                    href={eUserLocalNetworkLinkLogin}
                    target="_blank"
                    style={{ fontSize: '15px', fontWeight: 500 }}
                  >
                    Open Link
                  </Typography.Link>
                </Col>
              </Row>
            )}
            <RouterSingleRow
              title="Init Proxy URL"
              value={eUserInitProxyURL}
              valueSpecialClass={eUserInitProxyURL ? 'link-class' : undefined}
              copyValue={eUserInitProxyURL}
              onCopy={handleCopy}
            />
            {eUserInitProxyURL && (
              <Row gutter={[6, 16]} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                <Col span={6}></Col>
                <Col span={18}>
                  <Typography.Link
                    href={eUserInitProxyURL}
                    target="_blank"
                    style={{ fontSize: '15px', fontWeight: 500 }}
                  >
                    Open Link
                  </Typography.Link>
                </Col>
              </Row>
            )}
            <RouterSingleRow
              title="Voucher Check Proxy URL"
              value={eUserVoucherCheckProxyURL}
              valueSpecialClass={eUserVoucherCheckProxyURL ? 'link-class' : undefined}
              copyValue={eUserVoucherCheckProxyURL}
              onCopy={handleCopy}
            />
            {eUserVoucherCheckProxyURL && (
              <Row gutter={[6, 16]} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                <Col span={6}></Col>
                <Col span={18}>
                  <Typography.Link
                    href={eUserVoucherCheckProxyURL}
                    target="_blank"
                    style={{ fontSize: '15px', fontWeight: 500 }}
                  >
                    Open Link
                  </Typography.Link>
                </Col>
              </Row>
            )}
            {eUserOpenPageAfterSuccessLogin && (
              <>
                <RouterSingleRow
                  title="Open Page After Success Login"
                  value={eUserOpenPageAfterSuccessLogin}
                  valueSpecialClass="link-class"
                  copyValue={eUserOpenPageAfterSuccessLogin}
                  onCopy={handleCopy}
                />
                <Row gutter={[6, 16]} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                  <Col span={6}></Col>
                  <Col span={18}>
                    <Typography.Link
                      href={eUserOpenPageAfterSuccessLogin}
                      target="_blank"
                      style={{ fontSize: '15px', fontWeight: 500 }}
                    >
                      Open Link
                    </Typography.Link>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Col>

        {/* Attached Bot Card */}
        <Col span={24}>
          <Card
            className="border-card"
            title="Attached Bot"
            extra={
              <Button type="primary" onClick={handleAttachBotClick}>
                {attachedBot ? 'Update Bot' : 'Attach Bot'}
              </Button>
            }
          >
            {attachedBot ? (
              <>
                <RouterSingleRow title="Bot Name" value={attachedBot.radBotName} />
                <RouterSingleRow
                  title="Bot IP"
                  value={attachedBot.radBotIP}
                  copyValue={attachedBot.radBotIP}
                  onCopy={handleCopy}
                />
                <RouterSingleRow title="Auth Port" value={attachedBot.radBotAuthPort?.toString() || '-'} />
                <RouterSingleRow title="Acct Port" value={attachedBot.radBotAcctPort?.toString() || '-'} />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography.Text type="secondary">No bot attached to this router</Typography.Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Toggle Status Confirmation Modal */}
      <Modal
        title="Confirm Status Change"
        open={isToggleModalOpen}
        onOk={handleConfirmToggle}
        onCancel={handleCancelToggle}
        confirmLoading={toggleStatusLoading}
        okText="Confirm"
        cancelText="Cancel"
      >
        <Typography.Paragraph>
          {pendingEnableValue === 1
            ? 'Are you sure you want to enable this router? This will allow the router to process connections.'
            : 'Are you sure you want to disable this router? This will prevent the router from processing new connections. Existing connections may be affected.'}
        </Typography.Paragraph>
      </Modal>

      {/* Update Router Modal */}
      <Modal
        title="Update Router Details"
        open={isEditModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={700}
        maskClosable={!updateRouterLoading}
        closable={!updateRouterLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate} requiredMark={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Router Name</span>}
                name="routerName"
                rules={[
                  { required: true, message: 'Please enter router name' },
                  {
                    max: 100,
                    message: 'Router name must be less than 100 characters',
                  },
                ]}
              >
                <Input placeholder="Enter router name" disabled={updateRouterLoading} maxLength={100} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Vendor</span>}
                name="intPkCoreRouterVendorsID"
                rules={[{ required: true, message: 'Please select a vendor' }]}
              >
                <Select
                  placeholder="Select vendor"
                  disabled={updateRouterLoading || vendorsLoading}
                  loading={vendorsLoading}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={vendors.map((vendor: any) => ({
                    value: vendor.intPkCoreRouterVendorsID,
                    label: vendor.coreRouterVendorName,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Core Router IP</span>}
                name="coreRouterIp"
                rules={[
                  { required: true, message: 'Please enter core router IP' },
                  {
                    max: 45,
                    message: 'IP address must be less than 45 characters',
                  },
                ]}
              >
                <Input placeholder="Enter core router IP" disabled={updateRouterLoading} maxLength={45} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Username</span>}
                name="username"
                rules={[
                  { required: true, message: 'Please enter username' },
                  {
                    max: 45,
                    message: 'Username must be less than 45 characters',
                  },
                ]}
              >
                <Input placeholder="Enter username" disabled={updateRouterLoading} maxLength={45} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Password</span>}
            name="password"
            rules={[
              { required: true, message: 'Please enter password' },
              { max: 45, message: 'Password must be less than 45 characters' },
            ]}
          >
            <Input placeholder="Enter password" disabled={updateRouterLoading} maxLength={45} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Local Network Link Login</span>}
            name="eUserLocalNetworkLinkLogin"
            rules={[
              {
                required: true,
                message: 'Please enter local network link login',
              },
              { max: 300, message: 'Link must be less than 300 characters' },
            ]}
          >
            <Input
              placeholder="Enter local network link login"
              disabled={updateRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Init Proxy URL</span>}
            name="eUserInitProxyURL"
            rules={[
              { required: true, message: 'Please enter init proxy URL' },
              { max: 300, message: 'URL must be less than 300 characters' },
            ]}
          >
            <Input placeholder="Enter init proxy URL" disabled={updateRouterLoading} maxLength={300} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Voucher Check Proxy URL</span>}
            name="eUserVoucherCheckProxyURL"
            rules={[
              {
                required: true,
                message: 'Please enter voucher check proxy URL',
              },
              { max: 300, message: 'URL must be less than 300 characters' },
            ]}
          >
            <Input
              placeholder="Enter voucher check proxy URL"
              disabled={updateRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Open Page After Success Login</span>}
            name="eUserOpenPageAfterSuccessLogin"
            rules={[{ max: 300, message: 'URL must be less than 300 characters' }]}
          >
            <Input
              placeholder="Enter open page URL (optional)"
              disabled={updateRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Voucher Auth Bot Secret</span>}
            name="voucherAuthBotSecret"
            rules={[
              {
                required: true,
                message: 'Please enter voucher auth bot secret',
              },
              {
                max: 100,
                message: 'Voucher auth bot secret must be less than 100 characters',
              },
            ]}
          >
            <Input
              placeholder="Enter voucher auth bot secret"
              disabled={updateRouterLoading}
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Router Crypto Key</span>}
            name="routerCryptoKey"
            rules={[
              { required: true, message: 'Please enter router crypto key' },
              {
                max: 300,
                message: 'Crypto key must be less than 300 characters',
              },
            ]}
          >
            <Input placeholder="Enter router crypto key" disabled={updateRouterLoading} maxLength={300} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: '8px' }} disabled={updateRouterLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updateRouterLoading} disabled={updateRouterLoading}>
              {updateRouterLoading ? 'Updating...' : 'Update Router'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Attach Bot Modal */}
      <Modal
        title={attachedBot ? 'Update Attached Bot' : 'Attach Bot to Router'}
        open={isAttachBotModalOpen}
        onCancel={handleAttachBotCancel}
        footer={null}
        width={500}
        maskClosable={!attachBotLoading}
        closable={!attachBotLoading}
      >
        <Form form={attachBotForm} layout="vertical" onFinish={handleAttachBot} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Select Bot</span>}
            name="intPkRadBotNodeID"
            rules={[{ required: true, message: 'Please select a bot' }]}
          >
            <Select
              placeholder="Select a bot"
              disabled={attachBotLoading || botsLoading}
              loading={botsLoading}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={allBots.map((bot: any) => ({
                value: bot.intPkRadBotNodeID,
                label: `${bot.radBotName} (${bot.radBotIP})`,
              }))}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleAttachBotCancel} style={{ marginRight: '8px' }} disabled={attachBotLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={attachBotLoading} disabled={attachBotLoading}>
              {attachBotLoading ? 'Attaching...' : attachedBot ? 'Update Bot' : 'Attach Bot'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MasterStaffRouterDetailsComponent;
